// TradeRow.tsx
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import type { MarketTrade } from '../api/useMarketTrades';
import { TAG_COLORS } from '../api/sidebarTradeTypes';
import { Badge } from './shared';
import { plateLabel, tradeKey } from './charts/tradePlate';
import { formatVolume } from '../utils/format';
import { NEW_TAB_LINK_PROPS } from '../../../utils/newTab';
import type { SignalItem } from '../api/types';
import { TradeDetailContent } from './TradeDetailTooltip';

interface TradeRowProps {
    trade: MarketTrade;
    marketQuestion: string;
    /** The market's outcome name pair, for naming the trade side. */
    outcomes?: string[] | null;
    /** Gain in cents (e.g. +8 or -3). Undefined = don't show. */
    gain?: number;
    /** Account color for highlighted trader emphasis */
    traderColor?: string;
    /** Outcome chart color for market/outcome visual linkage */
    outcomeColor?: string;
    /** Is this a newly arrived trade (for flash animation)? */
    isNew?: boolean;
    /** Grey out this trade (non-highlighted account in All mode) */
    greyed?: boolean;
    /** Trader tags (e.g. ["High volume", "Insider"]) */
    tags?: string[];
    onClickMarket?: () => void;
    marketHref?: string;
    traderHref?: string;
    /** Toggle this account in the filter (click on row) */
    onToggleAccount?: () => void;
    /** Tooltip content rendered on address hover */
    tooltipContent?: React.ReactNode;
    /** Called when MUI Tooltip opens (address hover start) */
    onHoverAddress?: () => void;
    /** Called when MUI Tooltip closes (address hover end) */
    onLeaveAddress?: () => void;
    /** Signals matched to this trade by address + market + timestamp */
    matchedSignals?: SignalItem[];
    /** True when this trade's marker is hovered on the plot. */
    isTradeHovered?: boolean;
    /** Notified when this row is hovered/unhovered, for plot linking. */
    onTradeHover?: (key: string | null) => void;
}

function shortAddr(addr: string | undefined): string {
    if (!addr) return '—';
    if (addr.length <= 10) return addr;
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function formatTime(d: Date): string {
    const now = new Date();
    const isToday =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    const time = d.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    if (isToday) return time;
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
    return `${date} ${time}`;
}

export function TradeRow({
    trade,
    marketQuestion,
    outcomes,
    gain,
    traderColor,
    outcomeColor,
    isNew,
    greyed,
    tags,
    onClickMarket,
    marketHref,
    traderHref,
    onToggleAccount,
    tooltipContent,
    onHoverAddress,
    onLeaveAddress,
    matchedSignals,
    isTradeHovered,
    onTradeHover,
}: TradeRowProps) {
    const theme = useTheme();
    const key = tradeKey(trade);
    const plate = plateLabel(trade.side, trade.outcomeIndex, outcomes);

    const tooltipProps = {
        title: (tooltipContent ?? '') as React.ReactNode,
        enterDelay: 300,
        leaveDelay: 200,
        placement: 'right' as const,
        arrow: true,
        onClose: onLeaveAddress,
        slotProps: {
            tooltip: {
                sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: 8,
                    maxWidth: 320,
                    p: 1,
                },
            },
            arrow: {
                sx: { color: 'background.paper' },
            },
            popper: {
                sx: { pointerEvents: 'auto' },
            },
        },
    };

    return (
        <Box
            onClick={onToggleAccount}
            onMouseEnter={() => onTradeHover?.(key)}
            onMouseLeave={() => onTradeHover?.(null)}
            sx={{
                py: 0.75,
                px: 1,
                borderBottom: 1,
                borderColor: alpha(theme.palette.divider, 0.3),
                borderLeft: outcomeColor
                    ? `3px solid ${outcomeColor}`
                    : traderColor
                      ? `3px solid ${traderColor}`
                      : undefined,
                bgcolor: outcomeColor
                    ? alpha(outcomeColor, 0.04)
                    : traderColor
                      ? alpha(traderColor, 0.04)
                      : undefined,
                opacity: greyed ? 0.35 : 1,
                transition: 'opacity 0.2s',
                cursor: onToggleAccount ? 'pointer' : 'default',
                '&:hover': onToggleAccount
                    ? { bgcolor: alpha(theme.palette.action.hover, 0.08) }
                    : {},
                ...(isNew
                    ? { animation: 'rtHighlightFlash 1.2s ease-out forwards' }
                    : {}),
                // Plot -> list highlight when the matching marker is hovered.
                ...(isTradeHovered
                    ? {
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          boxShadow: `inset 0 0 0 1px ${alpha(
                              theme.palette.primary.main,
                              0.4
                          )}`,
                      }
                    : {}),
            }}
        >
            <Tooltip
                title={
                    <TradeDetailContent
                        trade={trade}
                        matchedSignals={matchedSignals}
                    />
                }
                enterDelay={500}
                leaveDelay={200}
                placement="right-start"
                arrow
                disableInteractive={false}
                slotProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: 1,
                            borderColor: 'divider',
                            boxShadow: 8,
                            maxWidth: 320,
                            p: 1,
                            pointerEvents: 'auto',
                        },
                    },
                    arrow: {
                        sx: { color: 'background.paper' },
                    },
                    popper: {
                        sx: { pointerEvents: 'auto', zIndex: 1200 },
                    },
                }}
            >
                <Box>
                    {/* Line 1: market question + timestamp */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.25,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                overflow: 'hidden',
                                maxWidth: '70%',
                            }}
                        >
                            {outcomeColor && (
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        minWidth: 6,
                                        borderRadius: '50%',
                                        bgcolor: outcomeColor,
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Typography
                                component={marketHref ? Link : 'span'}
                                {...(marketHref
                                    ? { to: marketHref, ...NEW_TAB_LINK_PROPS }
                                    : {})}
                                onClick={
                                    onClickMarket
                                        ? (e: React.MouseEvent) => {
                                              e.preventDefault();
                                              onClickMarket();
                                          }
                                        : undefined
                                }
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor:
                                        onClickMarket || marketHref
                                            ? 'pointer'
                                            : 'default',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    '&:hover':
                                        onClickMarket || marketHref
                                            ? { color: 'primary.main' }
                                            : {},
                                }}
                            >
                                {marketQuestion}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                fontFamily: 'monospace',
                                flexShrink: 0,
                            }}
                        >
                            {formatTime(trade.timestamp)}
                        </Typography>
                    </Box>

                    {/* Line 2: side + outcome, price, amount, gain, address */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            fontSize: '10px',
                        }}
                    >
                        <Badge
                            color={
                                trade.side === 'buy'
                                    ? theme.palette.success.main
                                    : theme.palette.error.main
                            }
                            title={
                                plate.text === plate.title
                                    ? undefined
                                    : plate.title
                            }
                        >
                            {plate.text}
                        </Badge>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                            }}
                        >
                            @{trade.price.toFixed(2)}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '10px',
                                fontFamily: 'monospace',
                                color: 'text.primary',
                            }}
                        >
                            {formatVolume(trade.quoteAmount)}
                        </Typography>
                        {tags &&
                            tags.map(tag => (
                                <Badge
                                    key={tag}
                                    color={
                                        TAG_COLORS[tag] ??
                                        theme.palette.text.secondary
                                    }
                                >
                                    {tag}
                                </Badge>
                            ))}
                        {gain !== undefined && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '10px',
                                    fontFamily: 'monospace',
                                    fontWeight: 600,
                                    color:
                                        gain >= 0
                                            ? 'success.main'
                                            : 'error.main',
                                }}
                            >
                                {gain >= 0 ? '+' : ''}
                                {gain.toFixed(0)}¢
                            </Typography>
                        )}
                        {outcomeColor && traderColor ? (
                            <Tooltip {...tooltipProps}>
                                <Box
                                    component={traderHref ? Link : 'span'}
                                    {...(traderHref
                                        ? {
                                              to: traderHref,
                                              target: '_blank',
                                              rel: 'noopener noreferrer',
                                          }
                                        : {})}
                                    onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                    }
                                    onMouseEnter={onHoverAddress}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        ml: 'auto',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        '&:visited': { color: 'inherit' },
                                        cursor: traderHref
                                            ? 'pointer'
                                            : 'default',
                                        '&:hover': traderHref
                                            ? { color: 'primary.main' }
                                            : {},
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: 5,
                                            height: 5,
                                            borderRadius: '50%',
                                            bgcolor: traderColor,
                                            display: 'inline-block',
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '9px',
                                            fontFamily: 'monospace',
                                            color: traderColor,
                                        }}
                                    >
                                        {shortAddr(trade.account)}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        ) : (
                            <Tooltip {...tooltipProps}>
                                <Typography
                                    component={traderHref ? Link : 'span'}
                                    {...(traderHref
                                        ? {
                                              to: traderHref,
                                              target: '_blank',
                                              rel: 'noopener noreferrer',
                                          }
                                        : {})}
                                    onClick={(e: React.MouseEvent) =>
                                        e.stopPropagation()
                                    }
                                    onMouseEnter={onHoverAddress}
                                    sx={{
                                        fontSize: '9px',
                                        fontFamily: 'monospace',
                                        color: 'text.disabled',
                                        textDecoration: 'none',
                                        '&:visited': { color: 'text.disabled' },
                                        ml: 'auto',
                                        cursor: traderHref
                                            ? 'pointer'
                                            : 'default',
                                        '&:hover': traderHref
                                            ? { color: 'primary.main' }
                                            : {},
                                    }}
                                >
                                    {shortAddr(trade.account)}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Tooltip>
        </Box>
    );
}
